// 앱 껍데기(apps/mobile)와 주고받는 브리지 — 계약은 @buildpy/bridge 한 벌, 웹에서 브리지를 다루는 곳은 여기 하나.
import { BRIDGE_VERSION, encode, NATIVE_EVENT, parseNativeToWeb, type NativeToWeb, type WebToNative } from "@buildpy/bridge";

type Body<M> = M extends WebToNative ? Omit<M, "v" | "id" | "key"> : never;
export type WebToNativeBody = Body<WebToNative>;
export type NativeHello = Omit<Extract<NativeToWeb, { type: "hello" }>["payload"], "key">;
export type NativeInsets = Extract<NativeToWeb, { type: "insets" }>["payload"];
type HelloMessage = Extract<NativeToWeb, { type: "hello" }>;
/** 구독자가 받는 앱 메시지 — hello 의 열쇠(key)는 뺀다. */
export type NativeMessage = Exclude<NativeToWeb, HelloMessage> | (Omit<HelloMessage, "payload"> & { payload: NativeHello });

interface NativeHost {
  postMessage: (data: string) => void;
}

/** 뒤로 판단에 쓰는 라우터 history 조각(TanStack Router 의 router.history). */
export interface BackHistory {
  canGoBack: () => boolean;
  back: () => void;
}

/** initNativeBridge 가 쓰는 라우터 조각 — 첫 화면이 그려지면 ready 를 보낸다. */
export interface BridgeRouter {
  history: BackHistory;
  subscribe: (event: "onResolved", fn: (e: { toLocation: { pathname: string } }) => void) => () => void;
}

const INSET_SIDES = ["top", "bottom", "left", "right"] as const;
// hello(열쇠) 전에 보낸 메시지는 이만큼만 쌓아 두었다가 열쇠를 받으면 보낸다.
const QUEUE_LIMIT = 20;
// 이만큼 기다려도 hello 가 없으면 우리 앱 껍데기가 아닌 것으로 보고 줄을 버린다 — 이후 보내기는 false(호출자가 폴백).
const HELLO_TIMEOUT_MS = 3000;
// 뒤로로 먼저 닫을 Radix 겹층 — 시트·다이얼로그·메뉴·셀렉트 목록.
const OPEN_OVERLAY = ["dialog", "alertdialog", "menu", "listbox"].map((role) => `[role="${role}"][data-state="open"]`).join(",");

const host = (): NativeHost | undefined =>
  typeof window === "undefined" ? undefined : (window as Window & { ReactNativeWebView?: NativeHost }).ReactNativeWebView;

const state: { hello: NativeHello | null; insets: NativeInsets | null; key: string | null; ready: WebToNativeBody | null; noBridge: boolean } = {
  hello: null,
  insets: null,
  key: null,
  ready: null,
  noBridge: false,
};
const queue: WebToNativeBody[] = [];
const listeners = new Set<(msg: NativeMessage) => void>();
let seq = 0;
let history: BackHistory | null = null;
let helloTimer: ReturnType<typeof setTimeout> | null = null;

export const isNativeApp = (): boolean => host() !== undefined;
export const nativeHello = (): NativeHello | null => state.hello;
export const nativeInsets = (): NativeInsets | null => state.insets;

const post = (target: NativeHost, key: string, msg: WebToNativeBody) => {
  seq += 1;
  target.postMessage(encode({ v: BRIDGE_VERSION, id: `w${Date.now().toString(36)}-${seq}`, key, ...msg } as WebToNative));
};

const armHelloTimeout = () => {
  if (helloTimer !== null || state.key || state.noBridge) return;
  helloTimer = setTimeout(() => {
    helloTimer = null;
    if (state.key) return;
    state.noBridge = true;
    queue.length = 0;
  }, HELLO_TIMEOUT_MS);
};

/** 앱 안이면 hello 로 받은 열쇠를 붙여 보내고(열쇠 전이면 줄 세워 두고) true · 브라우저거나 hello 가 끝내 없으면 false. */
export const sendToNative = (msg: WebToNativeBody): boolean => {
  const target = host();
  if (!target) return false;
  if (state.key) {
    post(target, state.key, msg);
    return true;
  }
  // ready 는 줄 밖에 따로 둔다 — 줄이 넘쳐도 밀려나지 않고, 늦게 온 hello 에도 보낸다.
  if (msg.type === "ready") state.ready = msg;
  else if (state.noBridge) return false;
  else if (queue.push(msg) > QUEUE_LIMIT) queue.shift();
  armHelloTimeout();
  return true;
};

const flushQueue = () => {
  const target = host();
  if (!target || !state.key) return;
  if (helloTimer !== null) clearTimeout(helloTimer);
  helloTimer = null;
  state.noBridge = false;
  const ready = state.ready;
  state.ready = null;
  for (const msg of ready ? [ready, ...queue.splice(0)] : queue.splice(0)) post(target, state.key, msg);
};

/** 앱이 보낸 메시지 구독 — 돌려받은 함수로 해제한다. */
export const onNativeMessage = (handler: (msg: NativeMessage) => void): (() => void) => {
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
};

export const pingNative = (): boolean => sendToNative({ type: "ping", payload: { at: Date.now() } });

export const openExternal = (url: string): void => {
  if (!sendToNative({ type: "openExternal", payload: { url } })) window.open(url, "_blank", "noopener,noreferrer");
};

/** 공유 — 앱이면 시스템 공유 시트 · 브라우저면 navigator.share(없으면 false). */
export const shareNative = async (data: { title: string; url: string }): Promise<boolean> => {
  if (sendToNative({ type: "share", payload: data })) return true;
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") return false;
  try {
    await navigator.share(data);
    return true;
  } catch {
    return false;
  }
};

const applyInsets = (insets: NativeInsets) => {
  const style = document.documentElement.style;
  for (const side of INSET_SIDES) style.setProperty(`--native-inset-${side}`, `${insets[side]}px`);
};

// 열린 겹층이 있으면 Escape 를 보낸다 — Radix 가 받아 닫으면(또는 닫기를 거부하면) preventDefault 하므로 그때만 처리한 것으로 본다.
const closeOverlay = (): boolean => {
  if (!document.querySelector(OPEN_OVERLAY)) return false;
  const escape = new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true });
  return !(document.activeElement ?? document.body).dispatchEvent(escape);
};

const goBack = (): boolean => {
  if (!history?.canGoBack()) return false;
  history.back();
  return true;
};

/** 앱 메시지 한 건 처리 — NATIVE_EVENT 의 event.data(JSON 문자열)를 그대로 받는다. 계약 밖이면 false. */
export const handleNativeMessage = (raw: unknown): boolean => {
  const msg = parseNativeToWeb(raw);
  if (!msg) return false;
  let out: NativeMessage = msg;
  switch (msg.type) {
    case "hello": {
      const { key, ...hello } = msg.payload;
      state.hello = hello;
      state.key = key;
      out = { ...msg, payload: hello };
      flushQueue();
      break;
    }
    case "insets":
      state.insets = msg.payload;
      applyInsets(msg.payload);
      break;
    case "back":
      sendToNative({ type: "backResult", payload: { handled: closeOverlay() || goBack() } });
      break;
    case "pong":
      console.info("[bridge] pong", Date.now() - msg.payload.at);
      break;
  }
  for (const listener of listeners) listener(out);
  return true;
};

const onNativeEvent = (e: Event) => void handleNativeMessage((e as MessageEvent).data);

/** main 에서 한 번 — 앱 메시지를 듣고, 첫 화면이 그려지면 ready{path} 를 한 번 보낸다. 돌려받은 함수로 걷는다. */
export const initNativeBridge = (router: BridgeRouter): (() => void) => {
  history = router.history;
  window.addEventListener(NATIVE_EVENT, onNativeEvent);
  let readySent = false;
  let unsubscribe: (() => void) | null = router.subscribe("onResolved", ({ toLocation }) => {
    if (readySent) return;
    readySent = true;
    unsubscribe?.();
    unsubscribe = null;
    sendToNative({ type: "ready", payload: { path: toLocation.pathname } });
  });
  return () => {
    unsubscribe?.();
    unsubscribe = null;
    window.removeEventListener(NATIVE_EVENT, onNativeEvent);
    history = null;
  };
};
